import { Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import type { LeaderboardEntry, LeaderboardResponse } from '@/api/friends'

interface LeaderboardDisplayProps {
  data: LeaderboardResponse | null
  isLoading: boolean
  error: Error | null
  onRetry: () => void
  className?: string
}

// Helper function to get avatar color
function getAvatarColor(name: string): string {
  const colors = [
    'bg-orange-600', // #D35400
    'bg-purple-800', // #6B2C5F
    'bg-blue-900',   // #1B4F72
    'bg-gray-600',   // #666
    'bg-amber-700',  // #8B4513
    'bg-indigo-600',
    'bg-green-700',
    'bg-red-700',
  ]
  const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return colors[index % colors.length]
}

export function LeaderboardDisplay({ 
  data, 
  isLoading, 
  error, 
  onRetry,
  className 
}: LeaderboardDisplayProps) {
  return (
    <div className={cn("min-h-[400px]", className)}>
      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="font-oswald text-base text-muted-foreground">
            Loading leaderboard...
          </p>
        </div>
      )}

      {/* Error State */}
      {!isLoading && error && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="text-6xl mb-4 opacity-30">⚠️</div>
          <h4 className="font-bebas-neue text-3xl tracking-[1px] uppercase mb-2 text-gray-600 dark:text-gray-400">
            Unable to Load Leaderboard
          </h4>
          <p className="font-oswald text-base text-gray-500 dark:text-gray-400 mb-6 text-center max-w-md">
            {error.message || 'Something went wrong while loading the leaderboard. Please try again.'}
          </p>
          <Button onClick={onRetry} className="gap-2">
            <Users className="h-4 w-4" />
            Try Again
          </Button>
        </div>
      )}

      {/* Empty State - No Data at all */}
      {!isLoading && !error && data && data.data.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="text-6xl mb-6 opacity-30">👥</div>
          <h4 className="font-bebas-neue text-4xl tracking-[1px] uppercase mb-2 text-gray-600 dark:text-gray-400">
            No Data Available
          </h4>
          <p className="font-oswald text-base text-gray-500 dark:text-gray-400 text-center max-w-md">
            Unable to load leaderboard data. Please try again.
          </p>
        </div>
      )}

      {/* Leaderboard Data - Always show if we have data (including just current user) */}
      {!isLoading && !error && data && data.data.length > 0 && (
        <>
          {/* No Friends Message - Show above leaderboard when user has no friends */}
          {data.metadata.totalFriends === 0 && (
            <div className="text-center mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
              <h4 className="font-bebas-neue text-2xl tracking-[1px] uppercase mb-2 text-blue-700 dark:text-blue-300">
                No Friends Yet
              </h4>
              <p className="font-oswald text-sm text-blue-600 dark:text-blue-400">
                Add friends to see them on the leaderboard! Use the "Manage Friends" button above to invite your friends and start competing.
              </p>
            </div>
          )}

          {/* Actual Leaderboard */}
        <div className="space-y-3">
          {data.data.map((entry: LeaderboardEntry) => {
            const name = `${entry.firstName || ''} ${entry.lastName || ''}`.trim()
            const initials = entry.avatar?.initials || `${entry.firstName?.[0] || ''}${entry.lastName?.[0] || ''}`.toUpperCase() || '?'
            
            return (
              <div
                key={entry.userId}
                className={cn(
                  'flex items-center p-4 rounded-xl transition-all duration-200',
                  entry.isCurrentUser
                    ? 'bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-200 dark:border-blue-700'
                    : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600',
                  !entry.hasPlayed && 'opacity-75' // Dim users who didn't play
                )}
              >
                {/* Rank */}
                <div className="flex items-center justify-center w-12 h-12 mr-4">
                  {entry.rank <= 3 ? (
                    <div className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg',
                      entry.rank === 1 && 'bg-yellow-500',
                      entry.rank === 2 && 'bg-gray-400',
                      entry.rank === 3 && 'bg-amber-600'
                    )}>
                      {entry.rank}
                    </div>
                  ) : (
                    <span className="font-bebas-neue text-2xl text-gray-600 dark:text-gray-400">
                      {entry.rank}
                    </span>
                  )}
                </div>

                {/* Avatar */}
                <Avatar className="h-12 w-12 mr-4">
                  <AvatarFallback 
                    className={cn(
                      'text-white font-semibold text-lg',
                      entry.avatar?.backgroundColor || getAvatarColor(name)
                    )}
                  >
                    {initials}
                  </AvatarFallback>
                </Avatar>

                {/* User Info */}
                <div className="flex-1">
                  <p className="font-oswald text-lg font-semibold">
                    {name}
                    {entry.isCurrentUser && (
                      <span className="text-blue-600 dark:text-blue-400 font-normal"> (you)</span>
                    )}
                  </p>
                </div>

                {/* Performance Metric */}
                <div className="text-right">
                  {entry.hasPlayed ? (
                    <>
                      <div className="font-bebas-neue text-2xl tracking-[1px] text-foreground">
                        {entry.performanceMetric.value}
                      </div>
                      <div className="font-oswald text-xs uppercase tracking-[0.5px] text-muted-foreground">
                        {entry.performanceMetric.type === 'streak' ? 'Day Streak' : 'Stars'}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-bebas-neue text-2xl tracking-[1px] text-muted-foreground">
                        --
                      </div>
                      <div className="font-oswald text-xs uppercase tracking-[0.5px] text-muted-foreground">
                        Not played
                      </div>
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Metadata */}
        {data.metadata.totalFriends > 0 && (
          <div className="mt-6 text-center">
            <p className="font-oswald text-sm text-muted-foreground">
              {data.metadata.totalFriends === 1 
                ? `Comparing with ${data.metadata.totalFriends} friend`
                : `Comparing with ${data.metadata.totalFriends} friends`
              }
              {data.metadata.gameTitle && data.metadata.gameTitle !== 'All Games' && 
                ` in ${data.metadata.gameTitle}`
              }
            </p>
          </div>
        )}
        </>
      )}
    </div>
  )
}

export default LeaderboardDisplay